(() => {
  'use strict';
  if (customElements.get('pb-case-purchase-bar')) return;

  class CasePurchaseBar extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.section = this.closest('[data-case-product]');
      this.mainButton = this.section?.querySelector('[data-case-main-add]');
      this.price = this.section?.querySelector('[data-product-detail-price]');
      this.button = this.querySelector('[data-case-bar-add]');
      this.barPrice = this.querySelector('[data-case-bar-price]');
      if (!this.mainButton || !this.button || !this.barPrice) return;
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      this.sync = () => {
        this.button.disabled = this.mainButton.disabled;
        const label = this.mainButton.textContent.trim();
        if (this.button.textContent !== label) this.button.textContent = label;
        const wrapper = this.section.querySelector('[data-price-wrapper]');
        const hiddenPrice = wrapper?.classList.contains('hide');
        const price = hiddenPrice ? '—' : (this.price?.textContent.trim() || '');
        if (this.barPrice.textContent !== price) this.barPrice.textContent = price;
      };
      this.button.addEventListener('click', () => {
        this.sync();
        if (this.mainButton.disabled) return;
        // Reuse the actual product form, selected variant, quantity and native
        // AJAX cart handler. No second cart request or replacement form.
        const form = this.mainButton.form;
        if (form?.requestSubmit) form.requestSubmit(this.mainButton);
        else this.mainButton.click();
      }, options);
      this.adjustChat = () => {
        const launcher = document.getElementById('fixify-live-chat-root')?.shadowRoot?.querySelector('.launch');
        const visible = !this.hidden && window.innerWidth < 768;
        if (this.chatLauncher && (!visible || this.chatLauncher !== launcher)) {
          this.chatLauncher.style.translate = this.originalChatTranslate;
          this.chatLauncher = null;
        }
        if (visible && launcher) {
          if (!this.chatLauncher) {
            this.chatLauncher = launcher;
            this.originalChatTranslate = launcher.style.translate;
          }
          launcher.style.translate = `0 -${Math.ceil(this.getBoundingClientRect().height) + (parseFloat(getComputedStyle(this).bottom) || 0) + 12}px`;
        }
      };
      this.updateVisibility = () => {
        const viewport = window.visualViewport;
        const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
        const keyboardOpen = editing && viewport && window.innerHeight - viewport.height > 150;
        const zoomed = viewport && viewport.scale > 1.05;
        this.hidden = !this.purchaseControlsPassed || keyboardOpen || zoomed;
        this.adjustChat();
      };
      window.addEventListener('resize', this.updateVisibility, options);
      window.visualViewport?.addEventListener('resize', this.updateVisibility, options);
      document.addEventListener('focusin', this.updateVisibility, options);
      document.addEventListener('focusout', this.updateVisibility, options);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.adjustChat);
        this.resizeObserver.observe(this);
      }
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.mainButton, {attributes:true, childList:true, subtree:true, characterData:true});
      const wrapper = this.section.querySelector('[data-price-wrapper]');
      if (wrapper) this.observer.observe(wrapper, {attributes:true, childList:true, subtree:true, characterData:true});
      if ('IntersectionObserver' in window) {
        this.visibility = new IntersectionObserver(entries => {
          const entry = entries[0];
          this.purchaseControlsPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.updateVisibility();
        });
        this.visibility.observe(this.mainButton);
      }
      // Without IntersectionObserver, leave the bar hidden; main controls work.
      this.sync();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.visibility?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.chatLauncher) this.chatLauncher.style.translate = this.originalChatTranslate;
      this.chatLauncher = null;
      this.controller = null;
      this.hidden = true;
    }
  }
  customElements.define('pb-case-purchase-bar', CasePurchaseBar);

  class CaseProtector extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.closest('form');
      this.toggle = this.querySelector('[data-case-protector-toggle]');
      this.mainButton = this.form?.querySelector('[data-case-main-add]');
      if (!this.form || !this.toggle || !this.mainButton) return;
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      this.update = () => {
        this.form.classList.toggle('pbcp-protector-selected', this.toggle.checked);
        this.querySelector('[data-case-protector-note]').hidden = !this.toggle.checked;
      };
      this.toggle.addEventListener('change', this.update, options);
      // Capture only opted-in submissions, before Boost's single-item handler.
      this.form.addEventListener('submit', event => {
        if (!this.toggle.checked && !this.busy) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.busy || this.mainButton.disabled) return;
        this.addBoth();
      }, {...options, capture:true});
      this.update();
      this.hidden = false;
    }
    async addBoth() {
      const error = this.querySelector('[data-case-protector-error]');
      const cartLink = this.querySelector('[data-case-protector-cart]');
      error.hidden = cartLink.hidden = true;
      const source = new FormData(this.form);
      const variant = source.get('id');
      const quantity = Number(source.get('quantity') || 1);
      if (!variant || !Number.isSafeInteger(quantity) || quantity < 1) {
        error.textContent = 'Please choose an available case and a valid quantity.';
        error.hidden = false;
        return;
      }
      const body = new FormData();
      body.append('items[0][id]', variant);
      body.append('items[0][quantity]', String(quantity));
      body.append('items[1][id]', this.dataset.variantId);
      body.append('items[1][quantity]', String(quantity));
      body.append('items[1][properties][For case]', this.dataset.caseTitle);
      const caseOption = this.form.querySelector('[data-product-select]')?.selectedOptions[0]?.textContent.trim();
      if (caseOption && caseOption !== 'Default Title') body.append('items[1][properties][Case option]', caseOption);
      // Preserve case line properties and selling plans on the case line only.
      for (const [key,value] of source.entries()) {
        if (key === 'selling_plan' || /^properties\[.*\]$/.test(key)) {
          const itemKey = key === 'selling_plan' ? 'items[0][selling_plan]' : key.replace(/^properties\[/, 'items[0][properties][');
          body.append(itemKey,value);
        }
      }
      this.busy = true;
      const originalLabel = this.mainButton.innerHTML;
      this.mainButton.disabled = true;
      this.mainButton.textContent = 'Adding both…';
      this.toggle.disabled = true;
      this.form.setAttribute('aria-busy','true');
      try {
        const response = await fetch(this.dataset.addUrl, {method:'POST',body,credentials:'same-origin',headers:{Accept:'application/json'}});
        if (!response.ok) throw new Error('Cart add failed');
        const result = await response.json();
        if (!Array.isArray(result.items) || result.items.length < 2) throw new Error('Unconfirmed cart response');
        this.mainButton.textContent = 'Added — opening your bag…';
        this.goToCart();
      } catch (failure) {
        // A stock or network failure may leave a partial cart. Never retry
        // automatically or submit the case alone without the customer's choice.
        error.textContent = 'We couldn’t confirm both items were added. Please check your bag before trying again.';
        error.hidden = cartLink.hidden = false;
        this.busy = false;
        this.mainButton.innerHTML = originalLabel;
        const selected = this.form.querySelector('[data-product-select]')?.selectedOptions[0];
        this.mainButton.disabled = !selected || selected.disabled;
      } finally {
        this.toggle.disabled = false;
        this.form.removeAttribute('aria-busy');
      }
    }
    goToCart() { window.location.assign(this.dataset.cartUrl); }
    disconnectedCallback() {
      this.controller?.abort();
      this.form?.classList.remove('pbcp-protector-selected');
      this.controller = null;
    }
  }
  customElements.define('pb-case-protector', CaseProtector);


  document.addEventListener('click', event => {
    const link = event.target.closest('[data-case-chat]');
    if (!link || !link.closest('[data-case-product]') || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const launcher = document.getElementById('fixify-live-chat-root')?.shadowRoot?.querySelector('.launch');
    if (!launcher) return; // Keep the actual contact-page fallback.
    event.preventDefault();
    if (launcher.getAttribute('aria-expanded') !== 'true') launcher.click();
    launcher.focus({preventScroll:true});
  });
})();
